import { useContext, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Main from "./components/layout/Main";
import "react-quill/dist/quill.snow.css";
import AppContext from "./store/app.context";
import axios from "axios";
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { notification } from "antd";
import RegisterPage from "./pages/auth/RegisterPage";
import LoginPage from "./pages/auth/LoginPage";
import DashboardPage from "./pages/dashboard";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import dayjs from "dayjs";
import { requestGetProfile, requestRefreshToken } from "./services/user.service";
import NotificationPage from "./pages/notifications";
import OrdersPage from "./pages/orders";
import InvoicesPage from "./pages/invoices";
import InstancesPage from "./pages/instances";
import DomainsPage from "./pages/domains";
import AppInfoPage from "./pages/settings/components/AppInformationTab";
import PublicInvoicePage from "./pages/public/invoice";
import SettingPage from "./pages/settings";
import * as dayjsID from "dayjs/locale/id";

dayjs.extend(utc);
dayjs.extend(tz);

dayjs.locale(dayjsID);
dayjs.tz.setDefault("Asia/Jakarta");

const firebaseConfig = {
	apiKey: "AIzaSyD7clRIvhAYSY74DHULdYZ4b75zRC6X3Jk",
	authDomain: "codeiva-cloudiva.firebaseapp.com",
	projectId: "codeiva-cloudiva",
	storageBucket: "codeiva-cloudiva.firebasestorage.app",
	messagingSenderId: "170182922333",
	appId: "1:170182922333:web:95d9d075df769cd4af7db4",
	measurementId: "G-GJFL6PY27L",
};

const vapidKey =
	"BA2XeeZNgQk-H2hPKuL_NxuaPJvSJrc-MJRqlnuUil2oyVA4sAXCXhmbWttqhwSqoGls1dvTRMqMk4bZk3t2IG0";

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Initialize messaging only if service workers are supported and in secure context
let messaging: any = null;
if (
	"serviceWorker" in navigator &&
	(window.location.protocol === "https:" || window.location.hostname === "localhost")
) {
	messaging = getMessaging(app);

	onMessage(messaging, (payload: any) => {
		const notification = new Notification("Cloudiva App", {
			body: payload?.data?.body || payload?.notification?.body,
			icon: "/favicon.png",
		});

		notification.onclick = () => {
			window.focus();
		};
	});
}

function App() {
	const context = useContext(AppContext);
	const navigate = useNavigate();

	axios.defaults.withCredentials = true;
	axios.defaults.headers.get["X-Language-Code"] = "id";
	axios.defaults.headers.get["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
	axios.defaults.headers.post["X-Language-Code"] = "id";
	axios.defaults.headers.post["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
	axios.defaults.headers.patch["X-Language-Code"] = "id";
	axios.defaults.headers.patch["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
	axios.defaults.headers.delete["X-Language-Code"] = "id";
	axios.defaults.headers.delete["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
	axios.interceptors.response.use(
		function (response) {
			return response;
		},
		function (error) {
			console.log(error.response);
			if (error.response.status === 401) {
				context.logout();
				navigate("/login");
			}
			return Promise.reject(error);
		}
	);

	useEffect(() => {
		if (context.isLoggedIn) {
			requestPermission();

			requestGetProfile().then((response) => {
				if (response.data && response.data.data) {
					context.setUser(response.data.data);
				}
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [context.isLoggedIn]);

	function requestPermission() {
		// Only request permission if messaging is available
		if (!messaging) {
			console.warn(
				"Firebase messaging not available. Service workers may not be supported or app is not in secure context."
			);
			return;
		}

		Notification.requestPermission().then((permission) => {
			const oldToken = localStorage.getItem("messaging_token");
			if (permission === "granted") {
				getToken(messaging, {
					vapidKey: vapidKey,
				})
					.then((currentToken) => {
						if (currentToken && currentToken !== oldToken) {
							localStorage.setItem("messaging_token", currentToken);

							requestRefreshToken(currentToken)
								.then((_response) => {
									logEvent(analytics, "messaging_token_updated", {
										token: currentToken,
										user: {
											id: context.user._id,
											name: context.user.name,
										},
									});
								})
								.catch((error) => {
									console.error("Error refreshing token: ", error);
								});
						}
					})
					.catch((error) => {
						console.error("Error getting messaging token: ", error);
					});
			} else {
				notification.error({
					message: "Izin ditolak!",
					description: "Anda tidak akan menerima notifikasi dari aplikasi ini.",
				});
			}
		});
	}

	return (
		<div className="App">
			<Routes>
				{!context.isLoggedIn && <Route path="/register" element={<RegisterPage />} />}
				{!context.isLoggedIn && <Route path="/login" element={<LoginPage />} />}
				{!context.isLoggedIn && <Route path="/forgot-password" element={<ForgotPasswordPage />} />}

				{/* User Start */}
				{context.isLoggedIn && (
					<>
						<Route
							path="/notifications"
							element={
								<Main>
									<NotificationPage />
								</Main>
							}
						/>
						<Route
							path="/dashboard"
							element={
								<Main>
									<DashboardPage />
								</Main>
							}
						/>
						<Route
							path="/:teamId/dashboard"
							element={
								<Main>
									<DashboardPage />
								</Main>
							}
						/>
						<Route
							path="/:teamId/orders/*"
							element={
								<Main>
									<OrdersPage />
								</Main>
							}
						/>
						<Route
							path="/:teamId/invoices/*"
							element={
								<Main>
									<InvoicesPage />
								</Main>
							}
						/>
						<Route
							path="/:teamId/instances/*"
							element={
								<Main>
									<InstancesPage />
								</Main>
							}
						/>
						<Route
							path="/:teamId/domains/*"
							element={
								<Main>
									<DomainsPage />
								</Main>
							}
						/>
						<Route
							path="/:teamId/settings"
							element={
								<Main>
									<SettingPage />
								</Main>
							}
						/>
						<Route
							path="/settings"
							element={
								<Main>
									<SettingPage />
								</Main>
							}
						/>

						<Route path="/*" element={<Navigate to="/dashboard" />} />
					</>
				)}
				{/* User Start */}

				{/* Default */}
				<Route path="/*" element={<Navigate to="/login" />} />
				{/* Default */}

				{/* Public Routes */}
				<Route path="/public/invoice/:token" element={<PublicInvoicePage />} />
			</Routes>
		</div>
	);
}

export default App;
